import clsx from 'clsx';
import { Chip, Tooltip } from 'copilot-design-system';
import React from 'react';

import { TagIcon } from 'src/legacy/components/Icons/TagIcon';
import { OptionChip } from 'src/legacy/components/MultiSelect/OptionChip';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { LightGray } from 'src/theme/colors';
import { isCategoricalColor } from 'src/utils/ColorUtils';

interface MultiSelectInputProps {
  selectedTags: Array<MultiSelectOption>;
  tagsLimit?: number;
  isCellContext?: boolean;
}

const OverflowRenderer = ({
  selectedTags,
}: {
  selectedTags: Array<MultiSelectOption>;
}) => {
  // When there's exactly one tag,
  // show the single tag normally
  if (selectedTags.length === 1) {
    const [tag] = selectedTags;
    return (
      <div className="w-full flex items-center cursor-pointer">
        <div className="p-1">
          <OptionChip
            className="[&>p]:truncate [&>p]:max-w-[70px]"
            label={tag.label}
            color={tag.color}
          />
        </div>
      </div>
    );
  }

  // When there are multiple tags, show a single chip
  // with "{n} tags" and a tooltip listing all tags
  return (
    <div className="w-full flex items-center cursor-pointer">
      <div className="p-1">
        <Tooltip
          position="top"
          content={
            <div className="flex flex-col gap-1">
              {selectedTags.map((tag) => (
                <Chip
                  className="w-fit"
                  key={tag.id}
                  label={tag.label}
                  variant="outlined"
                  color={tag.color}
                />
              ))}
            </div>
          }
        >
          <Chip
            label={`${selectedTags.length} tags`}
            variant="outlined"
            prefixIcon={
              <div className="relative flex items-center mr-3">
                {selectedTags
                  .filter((tag) => tag.color !== 'none')
                  .slice(0, 3)
                  .map((tag, index) => (
                    <TagIcon
                      key={tag.id}
                      style={{
                        fill: isCategoricalColor(tag.color)
                          ? `var(--${tag.color}-primary)`
                          : tag.color,
                        fontSize: 6,
                      }}
                      className={clsx(
                        'w-2 h-2 rounded-full absolute',
                        index > 0 ? 'left-[4px]' : '',
                      )}
                    />
                  ))}
              </div>
            }
          />
        </Tooltip>
      </div>
    </div>
  );
};

export const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  selectedTags,
  isCellContext,
  tagsLimit,
}) => {
  if (!selectedTags) return null;

  // when its cell renderer, e.g. crm table.
  // use the overflow renderer to show the tags.
  if (isCellContext) {
    return <OverflowRenderer selectedTags={selectedTags} />;
  }
  return selectedTags ? (
    <div className="w-full flex items-center flex-wrap cursor-pointer">
      {selectedTags.map((optionValue, index) => {
        const showOption = tagsLimit ? index < tagsLimit : true;
        return (
          showOption && (
            <div key={optionValue.id} className="p-1">
              <OptionChip
                className="[&>p]:truncate [&>p]:max-w-[70px]"
                label={optionValue.label}
                color={optionValue.color}
              />
            </div>
          )
        );
      })}
      {tagsLimit && (selectedTags?.length ?? 0) > tagsLimit && (
        <BaseTypography fontType="12Medium" style={{ color: LightGray }}>
          + {(selectedTags?.length ?? 0) - tagsLimit}
        </BaseTypography>
      )}
    </div>
  ) : null;
};
